import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  Calendar,
  CreditCard,
  File,
  FileText,
  Loader2,
  SquareCheckBig,
  Trash2,
  Users,
  Wrench,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  useDeleteNotification,
  useInfiniteNotificationsList,
  invalidateNotificationQueries,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useUnreadCount,
} from '@/hooks/useNotifications'
import { TopbarBellIcon } from '@/components/icons/topbar'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import {
  notificationDescription,
  notificationMeta,
  notificationTimestamp,
  notificationTitle,
  relativeNotificationTime,
} from '@/lib/notificationDisplay'
import type { AppNotification } from '@/types'

const PAGE_SIZE = 20

function stringValue(data: Record<string, unknown> | null, key: string) {
  const value = data?.[key]
  return typeof value === 'string' && value.trim() ? value : null
}

function notificationDestination(n: AppNotification): string | null {
  const data = n.data
  const detailUrl = stringValue(data, 'detail_url')

  if (detailUrl) {
    try {
      const parsed = new URL(detailUrl)
      return `${parsed.pathname}${parsed.search}`
    } catch {
      if (detailUrl.startsWith('/')) return detailUrl
    }
  }

  const jobId = stringValue(data, 'job_id')
  if (jobId) return `/calendar?job=${jobId}`

  const quoteId = stringValue(data, 'quote_id')
  if (quoteId) return `/quotes/${quoteId}`

  const contractId = stringValue(data, 'contract_id')
  if (contractId) return `/contracts/${contractId}`

  const invoiceId = stringValue(data, 'invoice_id')
  if (invoiceId) return `/invoices/${invoiceId}`

  const paymentId = stringValue(data, 'payment_id')
  if (paymentId) return `/payments/${paymentId}`

  const taskId = stringValue(data, 'task_id')
  if (taskId) return `/tasks/${taskId}`

  return null
}

function notificationStyle(type: string | null | undefined) {
  if (type?.includes('task')) {
    return {
      icon: SquareCheckBig,
      tone: 'task',
      backgroundColor: '#e0f2fe',
      color: '#0284c7',
    }
  }
  if (type?.includes('quote')) {
    return {
      icon: FileText,
      tone: 'quote',
      backgroundColor: '#fff4c7',
      color: '#a16207',
    }
  }
  if (type?.includes('payment') || type?.includes('invoice')) {
    return {
      icon: CreditCard,
      tone: 'payment',
      backgroundColor: '#d1fae5',
      color: '#047857',
    }
  }
  if (type?.includes('contract')) {
    return {
      icon: File,
      tone: 'contract',
      backgroundColor: '#ffedd5',
      color: '#ea580c',
    }
  }
  if (type?.includes('client')) {
    return {
      icon: Users,
      tone: 'client',
      backgroundColor: '#fae8ff',
      color: '#d946ef',
    }
  }
  if (type?.includes('technician')) {
    return {
      icon: Wrench,
      tone: 'technician',
      backgroundColor: '#ccfbf1',
      color: '#0f766e',
    }
  }

  return {
    icon: Calendar,
    tone: 'job',
    backgroundColor: '#fff1ea',
    color: '#ff5a1f',
  }
}

function NotificationRow({
  notification,
  onOpen,
  onDelete,
  deleting,
  deleteLabel,
}: {
  notification: AppNotification
  onOpen: (notification: AppNotification) => void
  onDelete: (notification: AppNotification) => void
  deleting: boolean
  deleteLabel: string
}) {
  const { i18n, t } = useTranslation('notifications')
  const isRead = notification.is_read || Boolean(notification.read_at)
  const meta = notificationMeta(notification)
  const style = notificationStyle(notification.type)
  const Icon = style.icon
  const title = notificationTitle(notification, t, i18n.language)
  const description = notificationDescription(notification, t, i18n.language)

  return (
    <li className='grid grid-cols-[40px_minmax(0,1fr)] items-start gap-3 px-4 py-3 transition-colors hover:bg-[#fff1ea]'>
      <button
        type='button'
        className='flex size-10 shrink-0 items-center justify-center rounded-xl p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        style={{
          backgroundColor: style.backgroundColor,
          color: style.color,
        }}
        onClick={() => onOpen(notification)}
        aria-label={title}
      >
        <Icon
          className='block h-4 min-h-4 w-4 min-w-4 shrink-0 stroke-[2.2]'
          aria-hidden
        />
      </button>

      <span className='min-w-0'>
        <span className='flex items-start justify-between gap-3'>
          <button
            type='button'
            className={cn(
              'flex min-w-0 items-center gap-1.5 text-left font-sans text-sm font-medium leading-5 tracking-normal focus-visible:outline-none',
              isRead ? 'text-[#171a20]' : 'text-[#ff5a1f]'
            )}
            onClick={() => onOpen(notification)}
          >
            <span className='truncate'>{title}</span>
            {!isRead && (
              <span className='size-1.5 shrink-0 rounded-full bg-[#ff5a1f]' />
            )}
          </button>

          <span className='flex shrink-0 items-center gap-2'>
            <span className='text-right text-xs font-normal leading-5 tracking-normal text-[#6c7285]'>
              {relativeNotificationTime(notification.created_at, i18n.language)}
            </span>
            <button
              type='button'
              className='flex size-4 shrink-0 items-center justify-center text-[#6c7285] transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              aria-label={deleteLabel}
              onClick={() => onDelete(notification)}
              disabled={deleting}
            >
              {deleting ? (
                <Loader2 className='size-4 animate-spin' />
              ) : (
                <Trash2 className='size-4' />
              )}
            </button>
          </span>
        </span>

        <button
          type='button'
          className='block w-full text-left font-sans focus-visible:outline-none'
          onClick={() => onOpen(notification)}
        >
          {meta.length > 0 && (
            <span className='mt-1 block text-xs font-normal leading-5 tracking-normal text-[#3d434e]'>
              {meta.join('  •  ')}
            </span>
          )}
          {description && (
            <span className='mt-1 line-clamp-2 text-xs font-normal leading-5 tracking-normal text-[#3d434e]'>
              {description}
            </span>
          )}
          <span className='mt-1 block text-xs font-normal leading-5 tracking-normal text-[#3d434e]'>
            {notificationTimestamp(notification.created_at, i18n.language)}
          </span>
        </button>
      </span>
    </li>
  )
}

// Figma 334:33367 — topbar notification popover with unread badge.
export function NotificationBell() {
  const { t } = useTranslation(['shell', 'notifications'])
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const { data: unread = 0 } = useUnreadCount()
  const unreadBadgeLabel = unread > 99 ? '99+' : String(unread)
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()
  const deleteNotification = useDeleteNotification()

  const {
    data,
    isFetching,
    isFetchingNextPage,
    isError,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteNotificationsList({ limit: PAGE_SIZE }, open)

  const notifications = useMemo(
    () => data?.pages.flatMap(page => page.data) ?? [],
    [data]
  )
  const initialLoading = isFetching && !data

  useEffect(() => {
    if (open) {
      void invalidateNotificationQueries(queryClient)
    }
  }, [open, queryClient])

  useEffect(() => {
    const list = listRef.current
    if (!list || !open || !hasNextPage || isFetchingNextPage) return

    if (list.scrollHeight <= list.clientHeight + 16) {
      void fetchNextPage()
    }
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    notifications.length,
    open,
  ])

  function onScroll() {
    const list = listRef.current
    if (!list || !hasNextPage || isFetchingNextPage) return
    const remaining = list.scrollHeight - list.scrollTop - list.clientHeight
    if (remaining <= 48) void fetchNextPage()
  }

  function onMarkAll() {
    markAll.mutate(undefined, {
      onError: e =>
        toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
    })
  }

  function onOpenNotification(notification: AppNotification) {
    const destination = notificationDestination(notification)
    if (!notification.read_at && !notification.is_read) {
      markRead.mutate(notification.id, {
        onError: e =>
          toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
      })
    }
    if (destination) {
      setOpen(false)
      navigate(destination)
    }
  }

  function onDelete(notification: AppNotification) {
    deleteNotification.mutate(notification.id, {
      onError: e =>
        toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
    })
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          className={cn(
            'relative flex size-10 shrink-0 items-center justify-center rounded-xl border bg-white p-0',
            'text-[#272c36] outline-none transition-colors hover:bg-[#fff1ea]',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            open ? 'border-[#ff5a1f] bg-[#fff1ea]' : 'border-[#e7e9ef]'
          )}
          aria-label={
            unread > 0
              ? t('shell:notificationBell.labelUnread', { count: unread })
              : t('shell:notificationBell.label')
          }
        >
          <TopbarBellIcon className='size-4' />
          {unread > 0 && (
            <span
              className={cn(
                'pointer-events-none absolute left-1/2 top-1/2 flex h-5 min-w-5 translate-x-[10%] -translate-y-[110%] items-center justify-center',
                'rounded-full border-2 border-white bg-[#ff5a1f] px-1',
                'font-sans text-[10] font-medium leading-none text-white'
              )}
              aria-hidden
            >
              {unreadBadgeLabel}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align='end'
        sideOffset={8}
        className='h-[400px] w-[420px] overflow-hidden rounded-xl border border-[#e7e9ef] bg-white p-0 shadow-[0_12px_32px_rgba(38,45,66,0.16)]'
      >
        <div className='flex h-14 items-center justify-between px-4'>
          <h2 className='text-sm font-semibold leading-5 text-[#171a20]'>
            {t('notifications:list.title')}
          </h2>
          <button
            type='button'
            className='text-sm font-medium leading-5 text-[#ff5a1f] transition-colors hover:text-[#dc3f10] disabled:cursor-not-allowed disabled:opacity-50'
            onClick={onMarkAll}
            disabled={markAll.isPending || unread === 0}
          >
            {t('notifications:list.markAllRead')}
          </button>
        </div>

        <div
          ref={listRef}
          className='h-[344px] overflow-y-auto overscroll-contain pr-1'
          onScroll={onScroll}
        >
          {isError ? (
            <p className='px-4 py-12 text-center text-sm text-destructive'>
              {t('notifications:list.loadError')}
            </p>
          ) : initialLoading ? (
            <div className='flex h-full items-center justify-center text-[#6c7285]'>
              <Loader2 className='size-4 animate-spin' />
            </div>
          ) : notifications.length === 0 ? (
            <p className='px-4 py-12 text-center text-sm text-[#6c7285]'>
              {t('notifications:list.empty')}
            </p>
          ) : (
            <>
              <ul>
                {notifications.map(notification => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onOpen={onOpenNotification}
                    onDelete={onDelete}
                    deleting={
                      deleteNotification.isPending &&
                      deleteNotification.variables === notification.id
                    }
                    deleteLabel={t('notifications:list.delete')}
                  />
                ))}
              </ul>
              {isFetchingNextPage && (
                <div className='flex items-center justify-center py-3 text-[#6c7285]'>
                  <Loader2 className='size-4 animate-spin' />
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default NotificationBell
